import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, GitCompare, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { AdmissionBadge } from '@/components/AddSourceModal';

const fmt = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const STATUS_DOT = {
  'Current':       '#03f37c',
  'Review needed': '#a3a3a3',
  'Conflict':      '#000000',
  'Held':          '#d4d4d4',
  'Superseded':    '#d4d4d4',
};

const FINDING_TYPE_STYLE = {
  'Changed':        'bg-gray-100 text-gray-700 border border-gray-200',
  'Conflict':       'bg-black text-white border border-black',
  'Stale':          'bg-gray-100 text-gray-600 border border-gray-200',
  'Missing source': 'bg-gray-50 text-gray-500 border border-gray-200',
};

const REVIEW_STATE_STYLE = {
  Pending:   'text-gray-400',
  Approved:  'font-medium',
  Rejected:  'text-gray-400',
  Held:      'text-gray-500',
  Escalated: 'text-gray-700 font-medium',
};

// ─── Version comparison panel (KS2-I3: uses generic /sources/versions/compare) ─

function ComparePanel({ sourceId, versions }) {
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [open, setOpen]         = useState(false);

  const canCompare = versionA && versionB && versionA !== versionB;

  const runCompare = () => {
    if (!canCompare) return;
    setLoading(true);
    setError(null);
    setResult(null);
    // KS2-I3: Use the generic source-version comparison endpoint.
    // Requires explicit source_id, baseline_version_id, target_version_id.
    api.post('/sources/versions/compare', {
      source_id: sourceId,
      baseline_version_id: versionA,
      target_version_id: versionB,
    })
      .then(r => { setResult(r.data); setOpen(true); })
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  const comparableVersions = versions.filter(v => v.extracted_passages || v.sha256 || v.id);

  if (comparableVersions.length < 2) return null;

  return (
    <section className="mb-10" data-testid="compare-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-heading">Version comparison</h2>
        <GitCompare size={16} style={{ color: '#a3a3a3' }} />
      </div>
      <p className="font-body text-xs text-gray-400 mb-4">
        Compares extracted passages — not summaries. Lost constraints and material conflicts
        are labelled as review signals, not conclusions.
        <strong className="text-gray-700 ml-1">Lexical/deterministic comparison — not semantic equivalence.</strong>
      </p>

      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>
            VERSION A
          </label>
          <select
            value={versionA}
            onChange={e => setVersionA(e.target.value)}
            className="rounded border px-3 py-2 font-body bg-white"
            style={{ fontSize: '12px', borderColor: '#d9dee6' }}
            data-testid="compare-version-a"
          >
            <option value="">Select…</option>
            {comparableVersions.map(v => (
              <option key={v.id} value={v.id}>{v.version_number || v.id.slice(0, 8)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>
            VERSION B
          </label>
          <select
            value={versionB}
            onChange={e => setVersionB(e.target.value)}
            className="rounded border px-3 py-2 font-body bg-white"
            style={{ fontSize: '12px', borderColor: '#d9dee6' }}
            data-testid="compare-version-b"
          >
            <option value="">Select…</option>
            {comparableVersions.map(v => (
              <option key={v.id} value={v.id}>{v.version_number || v.id.slice(0, 8)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={runCompare}
          disabled={!canCompare || loading}
          className="font-heading font-semibold px-4 py-2 rounded transition-colors"
          style={{
            backgroundColor: canCompare && !loading ? '#000' : '#e5e5e5',
            color: canCompare && !loading ? '#fff' : '#a3a3a3',
            fontSize: '12px',
          }}
          data-testid="compare-btn"
        >
          {loading ? 'Comparing…' : 'Compare'}
        </button>
      </div>

      {error && (
        <p className="font-body text-sm text-red-600 mb-3" data-testid="compare-error">{error}</p>
      )}

      {result && (
        <div data-testid="compare-result">
          {/* Disclosure */}
          {result.comparison_disclosure && (
            <div className="mb-3 p-2 rounded text-xs font-body text-gray-500 border" style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}>
              {result.comparison_disclosure}
            </div>
          )}
          {/* Summary */}
          <div className="flex flex-wrap gap-4 p-3 rounded mb-4"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e5e5' }}>
            <Stat label="Events detected" val={result.events_detected ?? result.summary?.total_added + result.summary?.total_removed + result.summary?.total_changed} color="#000" />
            <Stat label="Added" val={result.summary?.total_added ?? (result.events || []).filter(e => e.event_type === 'ADDED_INFORMATION').length} color="#027a48" />
            <Stat label="Removed" val={result.summary?.total_removed ?? (result.events || []).filter(e => e.event_type === 'REMOVED_INFORMATION').length} color="#b91c1c" />
            <Stat label="Modified" val={result.summary?.total_changed ?? (result.events || []).filter(e => e.event_type === 'MODIFIED_INFORMATION').length} color="#b45309" />
          </div>

          {/* Events from new /sources/versions/compare endpoint */}
          {(result.events || []).length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 font-heading font-medium text-black mb-2"
                style={{ fontSize: '12px' }}
                data-testid="toggle-signals-btn"
              >
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                Comparison events ({result.events.length})
              </button>
              {open && (
                <div className="space-y-2" data-testid="review-signals-list">
                  {result.events.map((ev, i) => (
                    <ComparisonEventRow key={ev.fingerprint || i} event={ev} index={i} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Legacy format (review_signals / conflicts) */}
          {(result.conflicts || []).map((c, i) => (
            <div
              key={i}
              className="rounded p-4 mb-3"
              style={{ backgroundColor: '#000', border: '1px solid #000' }}
              data-testid={`conflict-block-${i}`}
            >
              <p className="font-heading font-semibold" style={{ color: '#03f37c', fontSize: '12px', letterSpacing: '0.06em' }}>
                {c.verdict}
              </p>
              <p className="font-body mt-2" style={{ color: '#d4d4d4', fontSize: '12px' }}>{c.description}</p>
              {c.anchor && <p className="font-heading mt-1" style={{ color: '#737373', fontSize: '11px' }}>Anchor: {c.anchor}</p>}
            </div>
          ))}

          {(result.review_signals || []).length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 font-heading font-medium text-black mb-2"
                style={{ fontSize: '12px' }}
                data-testid="toggle-signals-btn"
              >
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                Review signals ({result.review_signals.length})
              </button>
              {open && (
                <div className="space-y-2" data-testid="review-signals-list">
                  {result.review_signals.map((s, i) => (
                    <div key={i} className="rounded p-3 flex gap-2"
                      style={{ backgroundColor: '#fff4e5', border: '1px solid #fcd34d22' }}
                      data-testid={`review-signal-${i}`}>
                      <AlertTriangle size={13} style={{ color: '#b45309', flexShrink: 0, marginTop: '1px' }} />
                      <div>
                        <p className="font-heading font-medium" style={{ color: '#b45309', fontSize: '11px', letterSpacing: '0.04em' }}>
                          {s.type.toUpperCase().replace('_', ' ')} — {s.severity}
                        </p>
                        <p className="font-body mt-0.5" style={{ fontSize: '12px', color: '#78350f' }}>{s.description}</p>
                        {s.old_text && (
                          <p className="font-body mt-1" style={{ fontSize: '11px', color: '#92400e', fontStyle: 'italic' }}>
                            Old: "{s.old_text.slice(0, 120)}{s.old_text.length > 120 ? '…' : ''}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Changed passages (legacy format) */}
          {(result.changed || []).length > 0 && (
            <div className="mb-4">
              <h3 className="font-heading font-medium text-black mb-2" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
                CHANGED PASSAGES ({result.changed.length})
              </h3>
              <div className="space-y-3" data-testid="changed-passages">
                {result.changed.slice(0, 5).map((c, i) => (
                  <div key={i} className="rounded overflow-hidden" style={{ border: '1px solid #e5e5e5' }}>
                    {c.anchor && <div className="px-3 py-1.5 font-heading" style={{ fontSize: '10px', letterSpacing: '0.05em', color: '#737373', backgroundColor: '#f9fafb' }}>
                      {c.anchor}
                    </div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2">
                      <div className="p-3" style={{ borderRight: '1px solid #e5e5e5', backgroundColor: '#fff8f8' }}>
                        <div className="font-heading mb-1" style={{ fontSize: '9px', color: '#b91c1c', letterSpacing: '0.06em' }}>REMOVED</div>
                        <p className="font-body" style={{ fontSize: '12px', color: '#374151' }}>{c.old?.text}</p>
                      </div>
                      <div className="p-3" style={{ backgroundColor: '#f0fdf6' }}>
                        <div className="font-heading mb-1" style={{ fontSize: '9px', color: '#027a48', letterSpacing: '0.06em' }}>ADDED</div>
                        <p className="font-body" style={{ fontSize: '12px', color: '#374151' }}>{c.new?.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4 text-xs font-body text-gray-400">
            {result.added?.length > 0 && <span data-testid="added-count">+{result.added.length} added passage{result.added.length !== 1 ? 's' : ''}</span>}
            {result.removed?.length > 0 && <span data-testid="removed-count">−{result.removed.length} removed passage{result.removed.length !== 1 ? 's' : ''}</span>}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── KS2-I3: Comparison event row with passage-level context ─────────────────
const EVENT_TYPE_COLORS = {
  ADDED_INFORMATION:   { bg: '#f0fdf6', border: '#bbf7d0', label: '#027a48' },
  REMOVED_INFORMATION: { bg: '#fff8f8', border: '#fca5a5', label: '#b91c1c' },
  MODIFIED_INFORMATION:{ bg: '#fff4e5', border: '#fcd34d', label: '#b45309' },
  LOST_CONSTRAINT:     { bg: '#fff1f2', border: '#fda4af', label: '#9f1239' },
  CONFLICT:            { bg: '#000',    border: '#000',    label: '#03f37c' },
  POSSIBLE_CONFLICT:   { bg: '#fefce8', border: '#fde047', label: '#854d0e' },
  POSSIBLE_DUPLICATE:  { bg: '#f5f3ff', border: '#c4b5fd', label: '#6d28d9' },
};
function ComparisonEventRow({ event, index }) {
  const [open, setOpen] = useState(false);
  const cfg = EVENT_TYPE_COLORS[event.event_type] || { bg: '#fafafa', border: '#e5e5e5', label: '#374151' };
  return (
    <div
      className="rounded border p-3"
      style={{ borderColor: cfg.border, backgroundColor: cfg.bg }}
      data-testid={`comparison-event-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          <span className="font-heading font-semibold" style={{ fontSize: '11px', color: cfg.label, letterSpacing: '0.05em' }}>
            {event.event_type}
          </span>
          {event.priority && (
            <span className="font-heading" style={{ fontSize: '9px', color: '#737373', letterSpacing: '0.06em', backgroundColor: '#f3f4f6', padding: '1px 6px', borderRadius: '3px' }}>
              PRIORITY: {event.priority}
            </span>
          )}
          {event.anchor && (
            <span className="font-heading text-gray-400" style={{ fontSize: '10px' }}>
              {event.anchor}
            </span>
          )}
          {event.review_state && event.review_state !== 'Pending' && (
            <span className="font-heading" style={{ fontSize: '9px', color: '#03f37c', letterSpacing: '0.05em', backgroundColor: '#f0fdf4', padding: '1px 5px', borderRadius: '3px' }}>
              {event.review_state}
            </span>
          )}
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          className="font-body text-gray-400 hover:text-black text-xs shrink-0"
          data-testid={`comparison-event-toggle-${index}`}
        >
          {open ? 'Less' : 'More'}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-2">
          {event.baseline_passage_text && (
            <div>
              <span className="font-heading" style={{ fontSize: '9px', color: '#b91c1c', letterSpacing: '0.05em' }}>BASELINE</span>
              <p className="font-body mt-0.5" style={{ fontSize: '12px', color: '#374151' }}>{event.baseline_passage_text}</p>
            </div>
          )}
          {event.target_passage_text && (
            <div>
              <span className="font-heading" style={{ fontSize: '9px', color: '#027a48', letterSpacing: '0.05em' }}>TARGET</span>
              <p className="font-body mt-0.5" style={{ fontSize: '12px', color: '#374151' }}>{event.target_passage_text}</p>
            </div>
          )}
          {event.description && (
            <p className="font-body text-gray-600" style={{ fontSize: '12px', fontStyle: 'italic' }}>
              {event.description}
            </p>
          )}
          <p className="font-body text-gray-400" style={{ fontSize: '11px' }}>
            Comparison mode: {event.comparison_mode || 'same_source'} —
            Lexical/deterministic, not semantic equivalence.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, val, color }) {  return (
    <div className="text-center">
      <div className="font-heading font-bold" style={{ fontSize: '20px', color }}>{val ?? 0}</div>
      <div className="font-body text-gray-500" style={{ fontSize: '11px' }}>{label}</div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function SourceDetail() {
  const { id } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get(`/canon/sources/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="font-body text-gray-400 py-8">Loading source…</p>;
  if (error)   return <p className="font-body text-gray-700 py-8">Error: {error}</p>;

  const { source, versions, findings } = data;

  return (
    <div data-testid="source-detail-page">
      <Link
        to="/app/canon"
        className="inline-flex items-center gap-1.5 font-body text-sm text-gray-400 hover:text-black mb-6 transition-colors focus-emerald"
        data-testid="back-to-canon"
      >
        <ArrowLeft size={14} />
        Canon
      </Link>

      {/* Source header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-start gap-3 mb-2">
          <h1 className="page-heading">{source.title}</h1>
          <span className="mt-1 inline-flex items-center gap-1.5">
            <span className="status-dot" style={{ backgroundColor: STATUS_DOT[source.status] || '#a3a3a3' }} />
            <span className="font-body text-gray-600 text-sm">{source.status}</span>
          </span>
          {source.admission_state && (
            <span className="mt-1"><AdmissionBadge state={source.admission_state} /></span>
          )}
        </div>
        <div className="flex flex-wrap gap-6 font-body text-sm text-gray-500">
          <span><strong className="text-gray-700 font-heading font-medium">Type:</strong> {source.source_type}</span>
          <span><strong className="text-gray-700 font-heading font-medium">Owner:</strong> {source.owner}</span>
          <span><strong className="text-gray-700 font-heading font-medium">Active version:</strong> {source.active_version}</span>
          <span><strong className="text-gray-700 font-heading font-medium">Last reviewed:</strong> {fmt(source.last_reviewed)}</span>
        </div>
      </div>

      {/* Version comparison panel */}
      <ComparePanel sourceId={id} versions={versions} />

      {/* Version history */}
      <section className="mb-10" data-testid="version-history">
        <h2 className="section-heading mb-4">Version history</h2>
        <p className="font-body text-xs text-gray-400 mb-4">
          Versions are immutable. A newly approved recommendation does not overwrite a prior version.
        </p>
        <div className="overflow-x-auto">
          <table className="ky-table">
            <thead>
              <tr>
                <th>Version</th><th>Created</th><th>Created by</th><th>Summary</th><th>Active</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} data-testid={`version-row-${v.id}`}>
                  <td>
                    <span className="font-heading font-semibold text-black">{v.version_number}</span>
                    {v.sha256 && (
                      <span className="ml-2 font-mono text-gray-400" style={{ fontSize: '10px' }}
                        title={v.sha256} data-testid={`version-sha-${v.id}`}>
                        {v.sha256.slice(0, 8)}…
                      </span>
                    )}
                    {v.is_immutable && (
                      <span className="ml-2 font-heading" style={{ fontSize: '9px', color: '#03f37c', letterSpacing: '0.06em' }}>
                        LOCKED
                      </span>
                    )}
                    {v.ingestion_artifact_id && (
                      <div className="mt-0.5">
                        <Link
                          to={`/app/artifact/${v.ingestion_artifact_id}`}
                          className="font-body text-gray-400 hover:text-black transition-colors"
                          style={{ fontSize: '10px' }}
                          data-testid={`version-artifact-link-${v.id}`}
                        >
                          ↳ artifact
                        </Link>
                      </div>
                    )}
                  </td>
                  <td className="font-body text-gray-500">{fmt(v.created_at)}</td>
                  <td className="font-body text-gray-600">{v.created_by}</td>
                  <td className="font-body text-gray-600 max-w-xs" style={{ maxWidth: '300px' }}>
                    <p className="line-clamp-2 text-xs">{v.content_summary}</p>
                  </td>
                  <td>
                    {v.is_active ? (
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#03f37c' }} title="Active version" />
                    ) : (
                      <span className="font-body text-gray-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Related findings */}
      <section data-testid="source-findings">
        <h2 className="section-heading mb-4">Related findings</h2>
        {findings.length === 0 ? (
          <p className="font-body text-gray-400 text-sm">No findings for this source.</p>
        ) : (
          <div className="space-y-3">
            {findings.map((f) => (
              <Link
                key={f.id}
                to={`/app/brain/${f.id}`}
                className="flex items-start gap-3 p-4 border border-gray-200 rounded hover:border-gray-400 transition-colors"
                data-testid={`source-finding-${f.id}`}
              >
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-heading font-medium mt-0.5 shrink-0 ${FINDING_TYPE_STYLE[f.finding_type] || 'bg-gray-100 text-gray-600'}`}>
                  {f.finding_type}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-medium text-black text-sm">{f.title}</p>
                  <p className="font-body text-gray-500 text-xs mt-0.5 line-clamp-1">{f.summary}</p>
                </div>
                <span
                  className={`shrink-0 text-xs font-heading ${REVIEW_STATE_STYLE[f.review_state] || 'text-gray-400'}`}
                  style={f.review_state === 'Approved' ? { color: '#03f37c' } : {}}
                >
                  {f.review_state}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
